import { isPlatformBrowser } from '@angular/common';
import { SwiperOptions } from 'swiper/types';
import { Subscription } from 'rxjs';

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges,
  OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation
} from '@angular/core';

import {
  DeviceService,
  IProductCard,
  ISliderInfo,
  SdLibConfig,
  SdThemeComponent,
  SizesEnum
} from '@seb/web-design-system/core';

import { DynamicContentShowMore, SdShowMoreComponent } from '@seb/web-design-system/show-more';
import { SdSliderComponent } from '@seb/web-design-system/sliders';
import { SdProductCardComponent } from '@seb/web-design-system/product-card';

@Component({
  selector: 'sd-cross-sell',
  templateUrl: './cross-sell.component.html',
  styleUrls: ['./cross-sell.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdSliderComponent,
    SdShowMoreComponent,
    SdProductCardComponent
  ]
})
export class SdCrossSellComponent extends SdThemeComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  isLoading = false;

  @Input()
  size: SizesEnum.m | SizesEnum.s = SizesEnum.s;

  @Input()
  showMoreDesktop: boolean;

  @Input()
  crossSellConfig: SwiperOptions;

  /**
   * Allow to manage border below the product
   */
  @Input()
  hasBorder = false;

  /**
   * Allow to manage the padding for the product card (if not on white BG)
   */
  @Input()
  isEmbedded = false;

  @Input()
  context = 'Cross-sell';

  @Input()
  products: IProductCard[];

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel: number;

  @Input()
  twicPicsAutoSize = true;

  @Input()
  isSSR = true;

  @Input()
  dynamicContentShowMore: DynamicContentShowMore;

  @ViewChild('crossSellSlider')
  crossSellSlider: SdSliderComponent;

  @Output()
  favorite = new EventEmitter<string>();

  @Output()
  cart = new EventEmitter<string>();

  @Output()
  notify = new EventEmitter<string>();

  @Output()
  slideNext = new EventEmitter<ISliderInfo>();

  @Output()
  slidePrev = new EventEmitter<ISliderInfo>();

  @Output()
  swiperInfo = new EventEmitter<ISliderInfo>();

  @Output()
  productClicked = new EventEmitter<string>();

  @Output()
  productSellerModal = new EventEmitter<boolean>();

  @Output()
  reviewsSeeAllClicked = new EventEmitter<{
    productCode: string;
    productName: string;
  }>();

  @Output()
  openRecommendedPriceModal = new EventEmitter<void>();

  isMobile$: boolean;
  sizesEnum = SizesEnum;
  mobileSubscription: Subscription;

  constructor(
    private cdRef: ChangeDetectorRef,
    private deviceService: DeviceService,
    private sdLibConfig: SdLibConfig
  ) {
    super();

    this.showMoreDesktop = this.sdLibConfig.get().showMoreDesktop;

    this.crossSellConfig = {
      pagination: {
        dynamicBullets: false,
        type: 'bullets',
        clickable: true
      },
      autoHeight: true,
      breakpoints: {
        0: {
          spaceBetween: 16,
          slidesPerView: 'auto'
        },
        768: {
          spaceBetween: 44
        },
        1280: {
          spaceBetween: 28
        }
      }
    } as SwiperOptions;
  }

  ngOnInit() {
    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (this.brand) {
      this.showMoreDesktop = this.sdLibConfig.get(this.brand).showMoreDesktop;
    }

    if (isPlatformBrowser(this.platformId)) {
      this.crossSellSlider?.refreshSlider();
    }
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
  }

  addToFavorite(product: string) {
    this.favorite.emit(product);
  }

  addToCart(product: string) {
    this.cart.emit(product);
  }

  stockNotify(product: string) {
    return this.notify.emit(product);
  }

  onSwiperInfo(event: ISliderInfo) {
    this.swiperInfo.emit(event);
  }

  onSlideNext(event: ISliderInfo) {
    this.slideNext.emit(event);
  }

  onSlidePrev(event: ISliderInfo) {
    this.slidePrev.emit(event);
  }

  onProductClick(event: string) {
    this.productClicked.emit(event);
  }

  toggleProductSellerModal(event) {
    this.productSellerModal.emit(event);
  }

  onReviewsSeeAllClicked(productCode: string, productName: string) {
    this.reviewsSeeAllClicked.emit({ productCode, productName });
  }

  onopenRecommendedPriceModal() {
    this.openRecommendedPriceModal.emit();
  }
}
